import { useEffect, useMemo, useState } from 'react'
import { Line } from 'react-chartjs-2'
import { useShallow } from 'zustand/react/shallow'

import { DBLTable, TelemetrySearchBar } from '@ui/components/elements'
import { ChartDataType, DataViewerProps } from '@ui/interfaces'
import { useTelemetryStore } from '@ui/stores'

interface AdditionalProps {
  tableType: 'input' | 'output' | 'data'
}

const initalChartData = {
  labels: [],
  datasets: [
    {
      label: 'Input',
      data: [],
      fill: true,
      backgroundColor: 'rgba(75,192,192,0.2)',
      borderColor: 'rgba(75,192,192,1)',
    },
  ],
} as ChartDataType

export const DataViewer = ({ data, tableType, isLoading, lineChart }: DataViewerProps & AdditionalProps) => {
  const dataSource = useTelemetryStore(useShallow((state) => state.dataSource))
  const [chartData, setChartData] = useState<ChartDataType>(initalChartData)

  // Set LineChart data
  useEffect(() => {
    const { timeseries } = lineChart || { timeseries: [] }

    const newChartData = {
      labels: timeseries ? timeseries.map((t) => new Date(t.timestamp).toLocaleTimeString()) : [],
      datasets: [
        {
          label: 'Input',
          data: timeseries ? timeseries.map((t) => t.volume) : [],
          fill: true,
          backgroundColor: 'rgba(75,192,192,0.2)',
          borderColor: 'rgba(75,192,192,1)',
        },
      ],
    }

    if (isLoading) {
      setChartData(initalChartData)
    } else {
      setChartData(newChartData)
    }
  }, [data, isLoading, lineChart])

  const primaryKeys = useMemo(() => {
    return dataSource === 'logs'
      ? ['resource[service.name]', 'resource[host.name]', 'body']
      : ['resource[service.name]', 'resource[host.name]', 'name']
  }, [dataSource])

  return (
    <div className="flex h-full w-full flex-col">
      {/* Search Bar */}
      <div className="flex w-full justify-end">
        <TelemetrySearchBar error={undefined} />
      </div>

      {/* Linechart */}
      <div className="my-2.5 h-48 max-h-[12vm] w-full rounded-md border border-gray-200 bg-white px-3">
        <Line
          data={chartData}
          options={{
            maintainAspectRatio: false,
            scales: {
              x: {
                grid: {
                  display: false,
                },
              },
            },
            plugins: {
              legend: {
                display: true,
                position: 'bottom',
                align: 'start',
              },
            },
          }}
        />
      </div>

      <div className="flex h-[calc(100vh-24rem)] w-[calc(100vw-12.25rem)] flex-row">
        <DBLTable
          tableData={data}
          loading={isLoading}
          dataSource={dataSource}
          tableType={tableType}
          defaultColumnOrder={primaryKeys}
        />
      </div>
    </div>
  )
}
